import { useState, FormEvent, ChangeEvent } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  SITE_CONFIG,
  SOCIAL_LINKS,
  PROJECT_TYPES,
  FORM_VALIDATION,
} from "@/lib/constants";
import { isValidEmail } from "@/lib/utils";
import type { ContactFormData, SubmitStatus } from "@/types";

export default function Contact() {
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    subject: "",
    message: "",
    projectType: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>("idle");

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!formData.name || !formData.email || !formData.message || !formData.projectType) {
      setSubmitStatus("error");
      return;
    }

    if (!isValidEmail(formData.email)) {
      setSubmitStatus("error");
      return;
    }

    if (formData.message.length > FORM_VALIDATION.messageMaxLength) {
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      // Simulate form submission delay
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Show success message without sending data
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
        projectType: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: "ri-mail-line",
      label: "Email",
      value: SITE_CONFIG.email,
      link: `mailto:${SITE_CONFIG.email}`,
    },
    {
      icon: "ri-phone-line",
      label: "Phone",
      value: SITE_CONFIG.phone,
      link: `tel:${SITE_CONFIG.phone.replace(/\D/g, "")}`,
    },
    {
      icon: "ri-map-pin-line",
      label: "Location",
      value: SITE_CONFIG.location,
      link: null,
    },
    {
      icon: "ri-time-line",
      label: "Timezone",
      value: SITE_CONFIG.timezone,
      link: null,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Get In Touch
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Ready to create something beautiful together? I'd love to hear about
            your project and discuss how we can bring your vision to life.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 font-inter">
                Send a Message
              </h2>
              <form
                id="contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      autoComplete="name"
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      autoComplete="email"
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="projectType"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Project Type *
                    </label>
                    <div className="relative">
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        {PROJECT_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line text-gray-400"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Subject
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                      placeholder="Brief subject line"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={FORM_VALIDATION.messageMaxLength}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm resize-vertical"
                    placeholder="Tell me about your project, timeline, and any specific requirements..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/
                    {FORM_VALIDATION.messageMaxLength} characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gray-900 text-white px-8 py-3 rounded-sm font-medium hover:bg-gray-800 disabled:bg-gray-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>

                {/* Status Messages */}
                {submitStatus === "success" && (
                  <div
                    className="p-4 bg-green-50 border border-green-200 rounded-sm"
                    role="alert"
                  >
                    <p className="text-green-800 text-sm">
                      Thank you for your message! I'll get back to you within{" "}
                      {FORM_VALIDATION.responseTimeHours} hours.
                    </p>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div
                    className="p-4 bg-red-50 border border-red-200 rounded-sm"
                    role="alert"
                  >
                    <p className="text-red-800 text-sm">
                      There was an error sending your message. Please check all
                      fields and try again.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6 font-inter">
                Contact Information
              </h2>

              <div className="space-y-6 mb-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="w-6 h-6 flex items-center justify-center text-gray-600 mt-1">
                      <i className={`${info.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 font-inter">
                        {info.label}
                      </h3>
                      {info.link ? (
                        <a
                          href={info.link}
                          className="text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-gray-700">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social Links */}
              <div className="mb-8">
                <h3 className="font-medium text-gray-900 mb-4 font-inter">
                  Follow My Work
                </h3>
                <div className="flex space-x-4">
                  {SOCIAL_LINKS.map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-900 hover:text-white transition-colors cursor-pointer"
                      aria-label={link.name}
                    >
                      <i className={`${link.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-gray-50 p-6 rounded-sm">
                <h3 className="font-medium text-gray-900 mb-2 font-inter">
                  Response Time
                </h3>
                <p className="text-gray-700 text-sm">
                  I typically respond to all inquiries within{" "}
                  {FORM_VALIDATION.responseTimeHours} hours. For urgent
                  projects, please mention it in your message or call directly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
